import React from "react";
import { ExcelColumn } from "../types";
import { FeatherCalendar, FeatherHash, FeatherType } from "@subframe/core";

interface FieldListSidebarProps {
  dimensions: ExcelColumn[];
  metrics: ExcelColumn[];
  onFieldClick?: (field: ExcelColumn) => void;
}

export const FieldListSidebar: React.FC<FieldListSidebarProps> = ({
  dimensions,
  metrics,
  onFieldClick,
}) => {
  const getFieldIcon = (type: string) => {
    switch (type) {
      case "date":
        return <FeatherCalendar className="w-4 h-4 text-blue-600" />;
      case "number":
        return <FeatherHash className="w-4 h-4 text-green-600" />;
      default:
        return <FeatherType className="w-4 h-4 text-gray-600" />;
    }
  };

  const renderFieldItem = (field: ExcelColumn) => {
    const isPreselected = field.isPreselected === true;
    
    return (
      <div
        key={field.id}
        onClick={() => onFieldClick?.(field)}
        className={`flex items-center gap-2 p-2 rounded-md border cursor-move transition-colors ${
          isPreselected
            ? "border-brand-300 bg-brand-50 hover:bg-brand-100 ring-2 ring-brand-200"
            : "border-neutral-border bg-default-background hover:bg-neutral-50"
        }`}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("application/json", JSON.stringify(field));
          e.dataTransfer.effectAllowed = "move";
        }}
        title={isPreselected ? "New column - recommended for your new pivot view" : field.name}
      >
        {getFieldIcon(field.type)}
        <span className={`text-body font-body flex-1 truncate ${
          isPreselected ? "text-brand-700 font-body-bold" : "text-default-font"
        }`}>
          {field.name}
        </span>
        {isPreselected && (
          <span className="px-1.5 py-0.5 text-xs bg-brand-100 text-brand-700 rounded">
            NEW
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-64 border-r border-neutral-border bg-default-background">
      {/* Header */}
      <div className="p-4 border-b border-neutral-border">
        <h3 className="text-heading-3 font-heading-3 text-default-font">
          Fields
        </h3>
        <p className="text-caption font-caption text-subtext-color mt-1">
          Drag fields to configure pivot
        </p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Dimensions Section */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <h4 className="text-body-bold font-body-bold text-default-font">
              Dimensions
            </h4>
            <span className="text-caption font-caption text-subtext-color">
              ({dimensions.length})
            </span>
          </div>
          <div className="space-y-1">
            {dimensions.map(renderFieldItem)}
          </div>
        </div>

        {/* Metrics Section */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <h4 className="text-body-bold font-body-bold text-default-font">
              Metrics
            </h4>
            <span className="text-caption font-caption text-subtext-color">
              ({metrics.length})
            </span>
          </div>
          <div className="space-y-1">
            {metrics.map(renderFieldItem)}
          </div>
        </div>
      </div>
    </div>
  );
};
